import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: '1. Information we collect',
    content: (
      <>
        <p>We collect only the information needed to operate the Goshen Prinath Academy platform, including:</p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>Account information such as name, role, class, term and authentication details.</li>
          <li>Academic information such as subjects, assessments, scores, results, attendance and teacher remarks.</li>
          <li>Information submitted through forms, support requests or communications with the Academy.</li>
          <li>Basic technical and usage information required for security, diagnostics and reliable service delivery.</li>
        </ul>
      </>
    ),
  },
  {
    title: '2. How we use information',
    content: (
      <p>
        We use information to authenticate users, provide role-based access, manage classes and academic records,
        display results, communicate school information, maintain platform security, resolve technical issues and
        improve the service.
      </p>
    ),
  },
  {
    title: '3. How information is shared',
    content: (
      <p>
        We do not sell personal information. Information may be available to authorised Academy administrators,
        teachers, students, parents or guardians according to their assigned roles. We may also use trusted service
        providers that support hosting, storage, security or communication, subject to appropriate confidentiality
        and data-protection obligations. Information may be disclosed where required by applicable law.
      </p>
    ),
  },
  {
    title: '4. Student and children’s privacy',
    content: (
      <p>
        The platform is intended for authorised school use. Student information is handled for legitimate educational
        and administrative purposes. Access is restricted by account role, and parents or guardians may contact the
        Academy concerning a student’s information or applicable privacy rights.
      </p>
    ),
  },
  {
    title: '5. Data security',
    content: (
      <p>
        We apply reasonable administrative, technical and organisational safeguards designed to protect information
        against unauthorised access, loss, alteration or disclosure. No electronic system is completely secure, so
        users must also protect their login credentials and report suspected misuse promptly.
      </p>
    ),
  },
  {
    title: '6. Data retention and deletion',
    content: (
      <p>
        Information is retained only for as long as reasonably necessary for educational, administrative, security
        and legal purposes. Users, parents or guardians may request access, correction or deletion where applicable.
        Some records may need to be retained to satisfy legitimate school or legal obligations.
      </p>
    ),
  },
  {
    title: '7. Your choices and rights',
    content: (
      <p>
        Subject to applicable law, you may request access to, correction of or deletion of personal information. You
        may also ask questions about how information is used. Requests may require identity or authority verification
        before they are completed.
      </p>
    ),
  },
  {
    title: '8. Updates to this policy',
    content: (
      <p>
        We may update this policy when our services, practices or legal obligations change. The revised policy will be
        published on this page with an updated effective date.
      </p>
    ),
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Goshen Prinath Academy';
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="text-sm font-semibold text-primary hover:opacity-80">
            Goshen Prinath Academy
          </Link>
          <Link
            to="/login-authentication"
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-primary hover:text-primary"
          >
            Back to login
          </Link>
        </div>
      </header>

      <section className="border-b border-slate-200 bg-gradient-to-br from-primary/10 via-white to-secondary/10">
        <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
          <span className="inline-flex rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Legal
          </span>
          <h1 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">Privacy Policy</h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-slate-600">
            This policy explains how Goshen Prinath Academy collects, uses, protects and manages information through
            its website, mobile application and connected school services.
          </p>
          <p className="mt-4 text-sm font-medium text-slate-500">Effective date: 12 August 2026</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="space-y-9 p-6 sm:p-10">
            <section>
              <h2 className="text-xl font-semibold text-slate-900">Our commitment</h2>
              <p className="mt-3 leading-7 text-slate-600">
                We respect the privacy of students, parents, guardians, teachers, staff and other authorised users.
                We process information responsibly and only for purposes connected with education, school
                administration, platform security and service delivery.
              </p>
            </section>

            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-xl font-semibold text-slate-900">{section.title}</h2>
                <div className="mt-3 leading-7 text-slate-600">{section.content}</div>
              </section>
            ))}

            <section className="rounded-xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
              <h2 className="text-xl font-semibold text-slate-900">9. Contact us</h2>
              <p className="mt-3 leading-7 text-slate-600">
                For privacy questions or requests, contact Goshen Prinath Academy through its official communication
                channels or use our contact page.
              </p>
              <Link to="/contact" className="mt-4 inline-flex font-semibold text-primary hover:underline">
                Contact the Academy
              </Link>
            </section>
          </div>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicy;
